import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate, useLocation } from 'react-router-dom';
// form
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { IFloor } from 'src/@types/Choqorak/Settings/Floor';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';

import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------

export default observer(function FloorNewEditForm() {
  const { FloorStore } = useStore();
  const { translate } = useLocales();
  const { createFloor, updateFloor, editMode, selectedFloor, clearSelectedFloor } = FloorStore;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewFloorSchema = Yup.object().shape({
    floorCode: Yup.string().required(`${translate('Validation.floorCode')}`),
  });

  const defaultValues = useMemo<IFloor>(
    () => ({
      floorId: selectedFloor?.floorId,
      floorCode: selectedFloor?.floorCode || '',
      floorMap: selectedFloor?.floorMap!,
    }),
    [selectedFloor]
  );

  const methods = useForm<IFloor>({
    resolver: yupResolver(NewFloorSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setValue,
    control,
    setError,
    watch,
    formState: { isSubmitting,errors},
  } = methods;

  const onSubmit = useThrottle(  (data: IFloor) => {
    const sanitizedData: IFloor = {
      ...data,
      buildingId: 1,
    };
    console.log(sanitizedData);

    if (data.floorId! === undefined) {
      ///create
      createFloor(sanitizedData).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
        navigate(PATH_DASHBOARD.Floor.list);
      }).catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
     
      });
    } else {
      ///update
      updateFloor(sanitizedData).then(() => {
        reset();
        enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
        navigate(PATH_DASHBOARD.Floor.list);
      }).catch((err) => {
        var json = JSON.parse(err.request.response);
        if (json.error != null) {
          setError('afterSubmit', { ...err, message: json.message });
        }
     
      });
    }
  });

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="floorCode"
                label={translate('Floor.FloorCode')}
                showAsterisk={true}
                autoFocus
              />

              {/* <input
                id="uploadFile"
                type="file"
                name="floorMap"
                onChange={(e) => {
                  const file = e.target.files![0];
                  setValue('floorMap', file);
                }}
              /> */}
              {/* <Controller
                name="floorMap"
                control={control}
                render={({ field }) => (
                  <>
                    <Button
                      component="label"
                      variant="contained"
                      startIcon={<MapIcon />}
                      sx={{ marginBottom: 2 }}
                    >
                      Upload Map
                      <input
                        type="file"
                        // accept=".jpg"
                        onChange={(e) => {
                          const file = e.target.files ? e.target.files[0] : null;
                          setValue('floorMap', file);
                        }}
                        style={{ display: 'none' }}
                      />
                    </Button>
                    <Typography variant="body2">{watch('floorMap')?.name || ''}</Typography>
                  </>
                )}
              /> */}

              <>
                {/* Hidden File Input */}
                <input
                  type="file"
                  name="floorMap"
                  // accept=".jpg"
                  id="contractattachment"
                  onChange={(e) => {
                    const file = e.target.files ? e.target.files[0] : null;
                    setValue('floorMap', file);
                  }}
                  style={{ display: 'none' }}
                />

                {/* TextField with Attachment Icon */}
                <TextField
                  label={translate('Floor.Attachfile')}
                  value={watch('floorMap')?.name || ''} // Display file name
                  placeholder={translate('Floor.NofileChosen')}
                  InputProps={{
                    readOnly: true, // Prevent manual input
                    startAdornment: (
                      <InputAdornment position="start">
                        {/* Attachment Icon */}
                        <label htmlFor="contractattachment">
                          <IconButton component="span">
                            <AttachFileIcon />
                          </IconButton>
                        </label>
                      </InputAdornment>
                    ),
                  }}
                  fullWidth
                  size="medium"
                  sx={{ width: '100%' }}
                />
              </>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedFloor();
                  navigate(PATH_DASHBOARD.Floor.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
